import StatsCheckpoint from "./statsCheckpoint";
import {
	isNumberOfAccessUnlimited,
	isNumberOfStrictAccessUnlimited
} from "../utils/checkpoints";
import TagGroupModel from "./tagGroups";

const ALL = "A";
const DISABLED = "D";
const IF_FORCED = "IFF";
export const INACTIVE_STATUS = 0;
export const ACTIVE_STATUS = 1;
export const ARCHIVE_STATUS = 2;

/**
 * Checkpoint.
 *
 * @param {Object} params - Object parameter
 * @param {string} params.id - Unique identifier
 * @param {string} params.name - Name of the checkpoint
 * @param {string} params.color - Hexadecimal color code
 * @param {integer} params.status - Status of the checkpoint
 * @param {array} params.rules - Checkpoint Rules
 * @param {integer} params.event - Cashless Event ID
 * @param {integer} params.default_nb_access - Number of access allowed by default
 * @param {boolean} param.is_using_strict_mode - Whether the checkpoint is in strict mode or not
 * @param {integer} params.barcode_type - Kind of barcode to scan
 * @param {integer} params.total_attendees - Total of attendees
 * @param {integer} params.cashless_zone_id - Cashless Zone ID
 * @param {integer} params.cashless_period_id - Cashless Period ID
 * @param {integer} params.pairing_mode - Pairing mode of the checkpoint
 * @param {integer} params.association_mode - Association mode of the checkpoint
 * @param {string} params.created - Date of creation
 * @param {integer} params.period_id - ID Period assiociated to the checkpoint
 * @param {integer} params.zone_id - ID Zone assiociated to the checkpoint
 * @param {array} params.populations_ids - ID of all populations assiociated to the checkpoint
 * @constructor
 */
export default class Checkpoint {
	constructor(checkpoint = {}, organizationId) {
		this.checkpoint = checkpoint;
		this.organizationId = organizationId;
		const {
			id,
			name,
			color,
			status,
			rules,
			tag_groups,
			event,
			default_nb_access,
			strict_nb_access,
			barcode_type,
			total_attendees,
			cashless_zone_id,
			cashless_period_id,
			pairing_mode,
			association_mode,
			allow_merging,
			anonymised_data,
			created,
			voucher_required,
			stats,
			integrations,
			supporting_document_rules,
			period_id,
			zone_id,
			events_in_rules,
			populations_ids,
			is_using_strict_mode,
			block_intralist_merge_mode,
			encryption_type
		} = checkpoint;
		this._id = id;
		this._name = name;
		this._color = color;
		this._status = status;
		this._barcodeType = barcode_type;
		this._totalAttendees = total_attendees;
		this._defaultNbAccess = default_nb_access;
		this._strictNbAccess = strict_nb_access;
		this._cashlessZoneId = cashless_zone_id;
		this._cashlessPeriodId = cashless_period_id;
		this._rules = rules ? rules.filter(r => r.removed === null) : [];
		this._tag_groups = Array.isArray(tag_groups)
			? tag_groups.map(group => new TagGroupModel(group))
			: [];
		this._event = event;
		this._pairingMode = pairing_mode;
		this._associationMode = association_mode;
		this._allowMerging = allow_merging;
		this._anonymisedData = anonymised_data;
		this._created = created;
		this._voucher_required = Boolean(voucher_required);
		this._stats = stats ? new StatsCheckpoint(stats) : null;
		this._integrations = integrations;
		this._supportingDocumentRules = supporting_document_rules;
		this._period_id = period_id;
		this._zone_id = zone_id;
		this._events_in_rules = events_in_rules;
		this._populations_ids = populations_ids;
		this._is_using_strict_mode = is_using_strict_mode;
		this._block_intralist_merge_mode = block_intralist_merge_mode;
		this._encryption_type = encryption_type || null;
	}

	id() {
		return this._id;
	}

	name() {
		return this._name;
	}

	color() {
		return this._color;
	}

	status() {
		return this._status;
	}

	barcodeType() {
		return this._barcodeType;
	}

	totalAttendees() {
		return this._totalAttendees;
	}

	countInAttendees() {
		return "--";
	}

	countOutAttendees() {
		return "--";
	}

	rules() {
		return this._rules;
	}

	tagGroups() {
		return this._tag_groups;
	}

	defaultNbAccess() {
		return this._defaultNbAccess;
	}

	strictNbAccess() {
		return this._strictNbAccess;
	}

	cashlessZoneId() {
		return this._cashlessZoneId;
	}

	cahslessPeriodId() {
		return this._cashlessPeriodId;
	}

	created() {
		return this._created;
	}

	event() {
		return this._event;
	}

	periodId() {
		return this._period_id;
	}

	zoneId() {
		return this._zone_id;
	}

	populationsIds() {
		return this._populations_ids;
	}

	eventsInRules() {
		return this._events_in_rules;
	}

	pairingMode() {
		return this._pairingMode;
	}

	associationMode() {
		return this._associationMode;
	}

	allowMerging() {
		return this._allowMerging;
	}

	anonymisedData() {
		return this._anonymisedData;
	}

	getEventId() {
		let defaultEventId = null;
		let event = this._event;
		let eventRule = (this.rules() || []).find(rule => rule.event);
		if (eventRule) {
			defaultEventId = eventRule.event;
		}

		return defaultEventId ? defaultEventId : event;
	}

	getDateId() {
		let eventRule = (this.rules() || []).find(rule => rule.event);
		if (eventRule && eventRule.date) {
			return eventRule.date;
		}
		return null;
	}

	toFormValues() {
		return {
			name: this._name,
			color: this._color,
			default_nb_access: isNumberOfAccessUnlimited(this._defaultNbAccess)
				? ""
				: this._defaultNbAccess,
			strict_nb_access: isNumberOfStrictAccessUnlimited(
				this._strictNbAccess
			)
				? null
				: this._strictNbAccess,
			is_using_strict_mode: this._is_using_strict_mode,
			rules: this._rules,
			cashless_zone_id: this._cashlessZoneId,
			cashless_period_id: this._cashlessPeriodId,
			event: this.getEventId(),
			barcode_type: this._barcodeType,
			pairing_mode: this._pairingMode ? this._pairingMode : "D",
			association_mode: this._associationMode
				? this._associationMode
				: "N",
			allow_merging: this._allowMerging,
			anonymised_data: this._anonymisedData,
			period: this._period,
			period_id: this._period_id,
			populations_ids: this._populations_ids,
			tag_groups: this._tag_groups,
			events_in_rules: this._events_in_rules,
			zone_id: this._zone_id,
			block_intralist_merge_mode: this._block_intralist_merge_mode,
			encryption_type: this._encryption_type
		};
	}

	isActive() {
		return this._status === ACTIVE_STATUS;
	}

	isArchive() {
		return this._status === ARCHIVE_STATUS;
	}

	getStats(stats) {
		if (stats) {
			return stats[this._id] ? stats[this._id] : null;
		}
		return null;
	}

	getStatistics() {
		return this._stats;
	}

	hasCashlessZoneId() {
		return Boolean(this._cashlessZoneId);
	}

	isNFC() {
		return Boolean(this._cashlessZoneId);
	}

	// TYPES OF THE LIST
	// IF_FORCED: pairing when cashless data exists
	// DISABLED: no pairing
	// ALL: pairing everyone

	isIfForced() {
		return this._pairingMode === IF_FORCED;
	}

	isDisabled() {
		return this._pairingMode === DISABLED;
	}

	isAll() {
		return this._pairingMode === ALL;
	}

	getIntegrations() {
		return this._integrations;
	}

	hasIntegrations() {
		const integrations = this.getIntegrations();
		return Boolean(integrations && integrations.length);
	}

	isUsingStrictMode() {
		return this._is_using_strict_mode;
	}

	blockIntralistMergeMode() {
		return this._block_intralist_merge_mode;
	}

	encryptionType() {
		return this._encryption_type;
	}
}
